// Copyright 2018 Sisense Inc. All rights reserved.
/* eslint-disable no-param-reassign, no-plusplus */

/**
 * @fileoverview A disposable implementation of a custom EventTarget.
 * Implementation based on Google Closure Library goog.events package
 * @link https://google.github.io/closure-library/api/goog.events.html
 * @author artem.lit@sisense.com (Artem Lytvynov)
 * @copyright sisense@sisense.com (Sisense Ltd.)
 * @license Sisense License
 */

'use strict';

const utils = require('@sbi/core.utils');
const { Event } = require('./event');
const { Disposable } = require('./disposable');
const { ListenerMap } = require('./listenermap');

/**
 * @typedef {import('./listener').Listener} Listener
 */

/**
 * Dispatches the given event on the ancestorsTree.
 * @param {!EventTarget} target The target to dispatch on.
 * @param {Event|string} eventOrType The event object or event type string.
 * @param {Array<EventTarget>=} opt_ancestorsTree The ancestors tree of the
 * target, in reverse order from the closest ancestor to the root event
 * target. May be null if the target has no ancestor.
 * @returns {boolean} If anyone called preventDefault on the event object (or
 * if any of the listeners returns false) this will also return false.
 * @private
 */
const dispatchEventInternal_ = function (target, eventOrType, opt_ancestorsTree) {
    let event = /** @type {Event} */(eventOrType);
    const type = event.type || /** @type {string} */ (eventOrType);

    // If accepting a string, create a custom event object so that
    // preventDefault and stopPropagation work with the event.
    if (utils.isString(eventOrType)) {
        event = new Event(type, target);
    } else {
        event.target = event.target || target;
    }

    // Executes all capture listeners on the ancestors, if any.
    let rv = true;
    let currentTarget;
    if (opt_ancestorsTree) {
        // eslint-disable-next-line
        for (let i = opt_ancestorsTree.length - 1; !event.propagationStopped_ && i >= 0; i--) {
            event.currentTarget = opt_ancestorsTree[i];
            currentTarget = event.currentTarget;
            // Here we call private method, but because we won't use
            // Closure Compiler, we can do it safety. From the other side,
            // this method will not be included to official API and
            // documentation.
            // noinspection JSAccessibilityCheck
            rv = currentTarget.fireListeners_(type, true, event) && rv;
        }
    }

    // Executes capture and bubble listeners on the target.
    if (!event.propagationStopped_) {
        // eslint-disable-next-line
        currentTarget = /** @type {EventTarget} */ (event.currentTarget = target);

        // See comment above.
        // noinspection JSAccessibilityCheck
        rv = currentTarget.fireListeners_(type, true, event) && rv;
        if (!event.propagationStopped_) {
            // See comment above.
            // noinspection JSAccessibilityCheck
            rv = currentTarget.fireListeners_(type, false, event) && rv;
        }
    }

    // Executes all bubble listeners on the ancestors, if any.
    if (opt_ancestorsTree) {
        // eslint-disable-next-line
        for (let i = 0; !event.propagationStopped_ && i < opt_ancestorsTree.length; i++) {
            event.currentTarget = opt_ancestorsTree[i];
            currentTarget = event.currentTarget;

            // See comment above.
            // noinspection JSAccessibilityCheck
            rv = currentTarget.fireListeners_(type, false, event) && rv;
        }
    }
    return rv;
};

/**
 * Custom implementation of EventTarget interface with full W3C support
 * (capture/bubble mechanism, stopping event propagation, preventing default
 * actions).
 *
 * You may subclass this class to turn your class into a listenable
 * EventTarget.
 *
 * Unless propagation is stopped, an event dispatched by an EventTarget will
 * bubble to the parent returned by {@code getParentEventTarget}. To set the
 * parent, call {@code setParentEventTarget}. Subclasses that don't support
 * changing the parent can override the setter to throw an error.
 *
 * Example usage:
 * <pre>
 *   let source = new EventTarget( );
 *   function handleEvent( e ){
     *     console.log( 'Type: ' + e.type + '; Target: ' + e.target );
     *   }
 *   source.addEventListener( 'foo', handleEvent );
 *   source.dispatchEvent( 'foo' );  // will call handleEvent
 *   source.removeEventListener( 'foo', handleEvent );
 * </pre>
 *
 * @extends {Disposable}
 */
class EventTarget extends Disposable {
    /**
     * @constructor
     */
    constructor() {
        super();

        /**
         * Maps of event type to an array of listeners.
         * @type {!ListenerMap}
         * @private
         */
        this.eventTargetListeners_ = new ListenerMap(this);

        /**
         * The object to use for event.target. Useful when mixing in an
         * EventTarget to another object.
         * @type {!EventTarget}
         * @private
         */
        this.actualEventTarget_ = this;

        /**
         * Parent event target, used during event bubbling.
         * @type {EventTarget|null}
         * @private
         */
        this.parentEventTarget_ = null;
    }

    /**
     * Fire listeners for specified type, event phase passing
     * {@code eventObject} as a parameter.
     * @param {string} type Listener type
     * @param {boolean} capture Capture/bubble mechanism flag
     * @param {Event} eventObject Event object
     * @returns {boolean} fire listener success result
     */
    fireListeners_(type, capture, eventObject) {
        let listenerArray = this.eventTargetListeners_.listeners[type];
        if (!listenerArray) {
            return true;
        }
        listenerArray = listenerArray.concat();
        let rv = true;
        for (let i = 0; i < listenerArray.length; ++i) {
            const listener = listenerArray[i];
            // We might not have a listener if the listener was removed.
            // eslint-disable-next-line
            if (listener && !listener.removed && listener.capture === capture) {
                const listenerFn = listener.listener;
                /*
                    TODO (buntarb): listener.src branch is highlighted by jest,
                                    can it be deleted?
                 */
                const listenerHandler = listener.handler || listener.src;
                rv = listenerFn
                    .call(listenerHandler, eventObject) !== false && rv;
            }
        }
        return rv && eventObject.returnValue_ !== false;
    }

    /**
     * Removes listeners from this object.  Classes that extend EventTarget
     * may need to override this method in order to remove references to DOM
     * Elements and additional listeners.
     * @override
     */
    disposeInternal() {
        super.disposeInternal();
        this.removeAllListeners();
        this.parentEventTarget_ = null;
    }

    /**
     * Returns unique id. The unique ID is guaranteed to be unique across
     * the current session.
     * @returns {number} Unique id
     */
    getUid() {
        return utils.getUid(this);
    }

    /**
     * Sets the parent of this event target to use for capture/bubble
     * mechanism.
     * @param {EventTarget|null} parent Parent EventTarget (null if none).
     * @returns {void}
     */
    setParentEventTarget(parent) {
        this.parentEventTarget_ = parent;
    }

    /**
     * Returns the parent of this event target to use for bubbling.
     * @returns {EventTarget|null} The parent EventTarget or null.
     */
    getParentEventTarget() {
        return this.parentEventTarget_;
    }

    /**
     * Returns root application (tree) EventTarget.
     * @returns {EventTarget} Root application EventTarget
     */
    getRootEventTarget() {
        let ancestor = this.getParentEventTarget();
        if (ancestor) {
            let node = ancestor;
            let ancestorCount = 1;
            for (; node; node = node.getParentEventTarget()) {
                if (++ancestorCount > EventTarget.MAX_ANCESTORS_) {
                    throw new Error('infinite loop');
                }
                ancestor = node;
            }
        } else {
            ancestor = this;
        }
        return ancestor;
    }

    /**
     * Determines whether the EventTarget is a root application node or not.
     * @returns {boolean} Is root application node flag
     */
    isRootEventTarget() {
        return this.getRootEventTarget() === this;
    }

    /**
     * Adds an event listener to the event target. The same handler can only
     * be added once per the type. Even if you add the same handler multiple
     * times using the same type then it will only be called once when the
     * event is dispatched.
     * @param {string} type The type of the event to listen for.
     * @param {Function} handler The function to handle the event. The
     * handler can also be an object that implements the handleEvent method
     * which takes the event object as argument.
     * @param {boolean=} opt_capture In DOM-compliant browsers, this
     * determines whether the listener is fired during the capture or bubble
     * phase of the event.
     * @param {Object=} opt_handlerScope Object in whose scope to call the
     * listener.
     * @returns {Listener} Listener object
     */
    addEventListener(type, handler, opt_capture, opt_handlerScope) {
        return this.eventTargetListeners_
            .add(type, handler, false, opt_capture, opt_handlerScope);
    }

    // noinspection JSUnusedGlobalSymbols
    /**
     * Removes an event listener from the event target. The handler must be
     * the same object as the one added. If the handler has not been added
     * then nothing is done.
     * event is dispatched.
     * @param {string} type The type of the event to listen for.
     * @param {Function} handler The function to handle the event. The
     * handler can also be an object that implements the handleEvent method
     * which takes the event object as argument.
     * @param {boolean=} opt_capture In DOM-compliant browsers, this
     * determines whether the listener is fired during the capture or bubble
     * phase of the event.
     * @param {Object=} opt_handlerScope Object in whose scope to call the
     * listener.
     * @returns {boolean} Is existed event listener was removed
     */
    removeEventListener(type, handler, opt_capture, opt_handlerScope) {
        return this.eventTargetListeners_.remove(type, handler, opt_capture, opt_handlerScope);
    }

    /**
     * Dispatch event.
     * @param {string|Event} e Event
     * @returns {boolean} Dispatch result status (false if anyone called preventDefault
     * or if any of the listeners returns false).
     */
    dispatchEvent(e) {
        let ancestorsTree;
        let ancestor = this.getParentEventTarget();

        if (ancestor) {
            ancestorsTree = [];
            let ancestorCount = 1;
            for (; ancestor; ancestor = ancestor.getParentEventTarget()) {
                ancestorsTree.push(ancestor);
                if (++ancestorCount > EventTarget.MAX_ANCESTORS_) {
                    throw new Error('infinite loop');
                }
            }
        }
        return dispatchEventInternal_(this.actualEventTarget_, e, ancestorsTree);
    }

    /**
     * Return listeners objects array for specified type and phase.
     * @param {string} type Listener type
     * @param {boolean} capture The capture mode of the listeners to retrieve.
     * @returns {Array<Listener>} Listeners objects array
     */
    getListeners(type, capture) {
        return this.eventTargetListeners_.getListeners(type, capture);
    }

    /**
     * Return specified listener object.
     * @param {string} type Listener type
     * @param {Function} listener Listener fn
     * @param {boolean} capture The capture mode of the listeners to retrieve.
     * @param {Object=} opt_listenerScope Listener scope
     * @returns {Listener} Listener
     */
    getListener(type, listener, capture, opt_listenerScope) {
        return this.eventTargetListeners_
            .getListener(type, listener, capture, opt_listenerScope);
    }

    /**
     * Determine if target has listeners for specified params.
     * @param {string=} opt_type Listener type
     * @param {boolean=} opt_capture The capture mode of the listeners to retrieve.
     * @returns {boolean} Result flag
     */
    hasListener(opt_type, opt_capture) {
        const type = utils.isDef(opt_type) ? opt_type : undefined;
        return this.eventTargetListeners_.hasListener(type, opt_capture);
    }

    /**
     * Stop listen the given listener object.
     * @param {Listener} listener Listener
     * @returns {boolean} Whether the listener is removed.
     */
    unlistenByListener(listener) {
        return this.eventTargetListeners_.removeByKey(listener);
    }

    /**
     * Remove all attached listeners.
     * @param {string=} opt_type Listener type
     * @returns {number} Number of listeners removed.
     */
    removeAllListeners(opt_type) {
        return this.eventTargetListeners_.removeAll(opt_type);
    }
}

/**
 * An artificial cap on the number of ancestors you can have. This is mainly
 * for loop detection.
 * @const {number}
 */
EventTarget.MAX_ANCESTORS_ = 1000;

// Exports:
exports.EventTarget = EventTarget;
